import { useEffect, useState } from 'react';
import { AppCacheProvider } from '@mui/material-nextjs/v15-pagesRouter';
import '../styles/theme.scss';
import { App as AppPropulsion } from '@ui-propulsion/react-components';
import { DiamondApp } from '@diamond-design/components';
import Head from 'next/head';
import http from 'lc-http';
import Footer from '../components/footer/footer';
import Disclaimer from '../components/disclaimer/disclaimer';
import LCHead from '../components/lcHead/lcHead';
import PartnerHeader from '../components/partnerHeader/partnerHeader';
import Header from '../components/header/header';
import CustHelpChat from '../components/custHelpChat/custHelpChat';
import XrayMode from '../components/xrayMode/xrayMode';
import Custom404 from '../components/custom-404/custom-404';
import setCookiesForPartnerPages from '../services/helpers/setCookiesForPartnerPages';
import { registerHeapEventProperties } from '../services/helpers/trackHeapEvent';
import { AppContext } from '../context';
import TrustedBadgeFooter from '../components/footer/trustedBadgeFooter/trustedBadgeFooter';
import rethinkSansFont from '../fonts/fonts';
import '../styles/globals.scss';
import useOracleHelpChat from '../hooks/useOracleHelpChat';
import '@diamond-design/components/dist/app/DiamondApp.scss';

function MyApp(props) {
  const { Component, pageProps } = props;
  const [loginCookie, setLoginCookie] = useState(false);
  useEffect(() => {
    http
      .get('/api/get-login-cookie-status')
      .then((response) => {
        const { data } = response;
        setLoginCookie(data.status ?? false);
      })
      .catch(() => {
        setLoginCookie(false);
      });
  }, []);

  const shouldShowOracleChat = !!pageProps?.oracleChatWidget;
  useOracleHelpChat(
    shouldShowOracleChat,
    pageProps.chatConfig,
    pageProps.chatScriptURL,
    pageProps.oracleChatWidget
  );

  useEffect(() => {
    setCookiesForPartnerPages();
  });

  useEffect(() => {
    registerHeapEventProperties();
  }, []);

  const getFooterComponent = () => {
    switch (pageProps?.footer?.type) {
      case 'Trusted Badge':
        return <TrustedBadgeFooter {...pageProps.footer} />;
      default:
        return <Footer {...pageProps.footer} />;
    }
  };
  return (
    // AppCacheProvider supplies the official Emotion cache
    // Its cache pins MUI styles to the <meta name="emotion-insertion-point">
    // tag rendered by DocumentHeadTags, so MUI base styles always load BEFORE the
    // Next.js CSS-module <link>s — letting our SCSS modules win the cascade injection-order race.
    <AppCacheProvider {...props}>
      <Head>
        <style global>{`
          :root {
            --font-primary: ${rethinkSansFont.style.fontFamily};
          }
        `}</style>
        {pageProps.pageBackground?.toLowerCase() === 'eggshell' && (
          <style global>{`
            body {
              background-color: var(--dds-palette-gray-50);
            }
          `}</style>
        )}
      </Head>
      {pageProps.seo && <LCHead {...pageProps.seo} />}
      {pageProps.csrfToken && (
        <Head>
          <meta name="csrf-header" content="csrf" />
          <meta name="csrf-token" content={pageProps.csrfToken} />
        </Head>
      )}
      <AppContext.Provider value={loginCookie}>
        <DiamondApp>
          <AppPropulsion>
            {pageProps.header &&
              (pageProps.header.type === 'partnerHeader' ? (
                <PartnerHeader {...pageProps.header} />
              ) : (
                <Header {...pageProps.header} />
              ))}
            {pageProps.notFound ? (
              <Custom404 content404={pageProps.content404} />
            ) : (
              <main>
                <Component {...pageProps} />
              </main>
            )}
            {pageProps.footer && getFooterComponent()}
            {pageProps.preview && pageProps.spaceId && pageProps.disclaimer && (
              <XrayMode
                title="disclaimer"
                spaceId={pageProps.spaceId}
                entryId={pageProps.entryId}
              />
            )}
            {pageProps.disclaimer && <Disclaimer {...pageProps.disclaimer} />}
            {pageProps?.tags?.includes('bankPage') && <CustHelpChat />}
          </AppPropulsion>
        </DiamondApp>
      </AppContext.Provider>
    </AppCacheProvider>
  );
}

export default MyApp;
